import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const PHONE_CODES = [
  { country: 'India', code: '+91' },
  { country: 'United States', code: '+1' },
  { country: 'United Kingdom', code: '+44' },
  { country: 'UAE', code: '+971' },
  { country: 'Singapore', code: '+65' },
  { country: 'Australia', code: '+61' },
  { country: 'Canada', code: '+1' },
  { country: 'Germany', code: '+49' },
  { country: 'France', code: '+33' },
];

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [phoneCode, setPhoneCode] = useState('+91');
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.phone) {
      toast.error('Phone number is required');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    
    try {
      const fullPhone = form.phone.startsWith('+') ? form.phone : `${phoneCode}${form.phone.replace(/\s+/g, '')}`;
      await register({ ...form, phone: fullPhone });
      toast.success('Welcome to Chaleur!', {
        description: 'You\'ve earned 100 welcome points.'
      });
      const redirect = searchParams.get('redirect') || '/account';
      navigate(redirect);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const redirectParam = searchParams.get('redirect');

  return (
    <div className="pt-32 pb-24 min-h-screen flex items-center" data-testid="register-page">
      <div className="chaleur-container">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-light mb-4">Join the Collective</h1>
            <p className="text-muted-foreground">Create your Chaleur account</p>
          </div>

          <div className="flex items-center gap-4 p-4 bg-accent/30 border border-accent/50 mb-8">
            <Gift className="w-8 h-8 text-primary flex-shrink-0" />
            <div>
              <p className="font-medium text-sm">Welcome Bonus</p>
              <p className="text-xs text-muted-foreground">Earn 100 points when you create an account</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-sm mb-2 block">First Name *</Label>
                <Input
                  id="first_name"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="input-chaleur"
                  required
                  placeholder="First name"
                  data-testid="register-firstname"
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-sm mb-2 block">Last Name *</Label>
                <Input
                  id="last_name"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="input-chaleur"
                  required
                  placeholder="Last name"
                  data-testid="register-lastname"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-sm mb-2 block">Email *</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-chaleur"
                required
                placeholder="your@email.com"
                data-testid="register-email"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-sm mb-2 block">Phone *</Label>
              <div className="flex">
                <Select value={phoneCode} onValueChange={setPhoneCode}>
                  <SelectTrigger className="w-[100px] rounded-none rounded-l-md border-r-0" data-testid="register-phone-code">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PHONE_CODES.map(({ country, code }) => (
                      <SelectItem key={`${country}-${code}`} value={code}>
                        {code} {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="input-chaleur rounded-l-none border-l-0"
                  required
                  placeholder="98765 43210"
                  data-testid="register-phone"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm mb-2 block">Password *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input-chaleur pr-10"
                  required
                  minLength={8}
                  placeholder="Minimum 8 characters"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {form.password.length > 0 && form.password.length < 8 && (
                <p className="text-xs text-red-500 mt-1">Password must be at least 8 characters</p>
              )}
              {form.password.length >= 8 && (
                <p className="text-xs text-green-600 mt-1">Password strength: Good</p>
              )}
            </div>

            <Button type="submit" className="btn-chaleur-primary w-full" disabled={loading} data-testid="register-btn">
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to={`/login${redirectParam ? `?redirect=${redirectParam}` : ''}`} className="text-primary hover:underline" data-testid="signin-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
